import type { FC, ReactNode } from 'react'
import type { Profile, Skill, Tool } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight } from 'lucide-react'

import {
    motion,
    useScroll,
    useSpring,
    useTransform
} from 'motion/react'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { SectionLabel } from '@/components/generals/section-label'
import { Star } from '@/components/generals/star'
import { useMounted } from '@/hooks/use-mounted'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

interface SkillsSectionProps {
    profile: Profile | null
    skills: Skill[]
    tools: Tool[]
}

interface SkillCardProps {
    skill: Skill
    index: number
}

interface ToolboxCardProps {
    tools: Tool[]
}

const CARD_STYLES = [
    'bg-white text-foreground ring-1 ring-foreground/10',
    'bg-accent text-accent-foreground',
    'bg-primary text-primary-foreground',
    'bg-secondary text-secondary-foreground',
    'bg-foreground text-background'
]

const CARD_BASE = 'min-h-[clamp(300px,52svh,440px)] shrink-0 snap-start rounded-[2rem]'

const pad = (value: number): string => String(value).padStart(2, '0')

const SkillCard: FC<SkillCardProps> = ({ skill, index }): ReactNode => {
    return (
        <article
            className={cn(
                CARD_BASE,
                'group relative flex w-[78vw] flex-col justify-between gap-10 overflow-hidden p-7 sm:w-[360px] md:w-[400px] md:p-9',
                CARD_STYLES[index % CARD_STYLES.length]
            )}
        >
            <div className='flex items-start justify-between gap-4'>
                <span aria-hidden='true' className='text-outline font-heading text-7xl font-extrabold leading-none tracking-[-0.04em] md:text-8xl'>
                    {pad(index + 1)}
                </span>

                <Star className='size-8 shrink-0 transition-transform duration-700 group-hover:rotate-180' />
            </div>

            <div>
                <h3 className='font-heading text-2xl font-bold leading-tight md:text-3xl'>
                    <span data-mdlfy-cms={`skills:${skill.id}:title`}>{skill.title}</span>
                </h3>

                {skill.description && (
                    <p data-mdlfy-cms={`skills:${skill.id}:description`} className='mt-3 leading-relaxed'>
                        {skill.description}
                    </p>
                )}
            </div>
        </article>
    )
}

const ToolboxCard: FC<ToolboxCardProps> = ({ tools }): ReactNode => {
    return (
        <article className={cn(CARD_BASE, 'flex w-[86vw] flex-col justify-between gap-8 border-2 border-dashed border-foreground/20 p-7 sm:w-[480px] md:w-[560px] md:p-9')}>
            <div>
                <p className='font-mono text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground'>
                    Toolbox
                </p>

                <h3 className='mt-3 font-heading text-3xl font-bold leading-tight md:text-4xl'>
                    Tools I use every day
                </h3>
            </div>

            <ul className='flex flex-wrap gap-2.5'>
                {tools.map((tool, index) => (
                    <motion.li
                        key={tool.id}
                        initial={{ opacity: 0, scale: 0.6, rotate: index % 2 === 0 ? -8 : 8 }}
                        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                        viewport={{ once: true, amount: 0.6 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 18, delay: index * 0.04 }}
                        className='rounded-full bg-cream px-4 py-2 text-sm font-semibold ring-1 ring-foreground/10 md:text-base'
                    >
                        <span data-mdlfy-cms={`tools:${tool.id}:name`}>{tool.name}</span>
                    </motion.li>
                ))}
            </ul>
        </article>
    )
}

// Vertical scroll drives a horizontal track: the section pins to the screen
// and the skill cards slide sideways until the toolbox comes in.
export const SkillsSection: FC<SkillsSectionProps> = ({ profile, skills, tools }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const mounted = useMounted()
    const reduced = usePrefersReducedMotion()
    const [distance, setDistance] = useState(0)
    const pinned = mounted && !reduced && distance > 0

    useEffect(() => {
        const track = trackRef.current

        if (!track) return

        const measure = () => {
            setDistance(Math.max(0, Math.round(track.scrollWidth - document.documentElement.clientWidth)))
        }

        const observer = new ResizeObserver(measure)

        measure()
        observer.observe(track)
        window.addEventListener('resize', measure)

        return () => {
            observer.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [])

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] })
    const smooth = useSpring(scrollYProgress, { stiffness: 160, damping: 32, restDelta: 0.0005 })
    const x = useTransform(smooth, value => pinned ? -distance * value : 0)

    return (
        <section
            id='skills'
            ref={sectionRef}
            className='relative bg-background'
            style={pinned ? { height: `calc(100svh + ${distance}px)` } : undefined}
        >
            <div className={cn(pinned ? 'sticky top-0 flex h-[100svh] flex-col justify-center overflow-hidden py-10' : 'py-20 md:py-32')}>
                <Container>
                    <SectionLabel index='04' label='Skills' />

                    <div className='mt-6 flex flex-wrap items-end justify-between gap-4'>
                        {profile?.skills_title && (
                            <h2 className='max-w-3xl font-heading text-[clamp(2.4rem,6vw,5rem)] font-extrabold leading-[0.95] tracking-[-0.035em]'>
                                <span data-mdlfy-cms={`profile:${profile.id}:skills_title`}>{profile.skills_title}</span>
                            </h2>
                        )}

                        <p aria-hidden='true' className='hidden items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground md:flex'>
                            {pinned ? 'Keep scrolling' : 'Swipe to explore'}
                            <ArrowRight className='size-4' />
                        </p>
                    </div>
                </Container>

                <div className={cn('mt-8 md:mt-12', !pinned && 'no-scrollbar snap-x snap-mandatory overflow-x-auto')}>
                    <motion.div
                        ref={trackRef}
                        style={{ x }}
                        className='flex w-max gap-4 px-5 sm:px-8 md:gap-6 xl:px-[calc((100vw_-_80rem)/2_+_2rem)]'
                    >
                        {skills.map((skill, index) => (
                            <SkillCard key={skill.id} skill={skill} index={index} />
                        ))}

                        {tools.length > 0 && <ToolboxCard tools={tools} />}
                    </motion.div>
                </div>

                {pinned && (
                    <Container className='mt-8 md:mt-10'>
                        <div aria-hidden='true' className='h-1 w-full overflow-hidden rounded-full bg-foreground/10'>
                            <motion.div className='h-full origin-left rounded-full bg-secondary' style={{ scaleX: smooth }} />
                        </div>
                    </Container>
                )}
            </div>
        </section>
    )
}
